import { BaseScene } from './BaseScene';
import { EarthSelectionPanel, EarthOption } from '../ui/components/EarthSelectionPanel';
import { t, setLang, getLang } from '../i18n/lang';
import { UserProfile } from '../entities/base/UserProfile';

const EARTH_OPTIONS: EarthOption[] = [
  {
    key: 'classic',
    name: '',
    description: '',
    health: 1000,
    damageResistance: 0,
    towerLimit: 8,
    specialAbility: 'None',
  },
  {
    key: 'ice',
    name: '',
    description: '',
    health: 1500,
    damageResistance: 0.1,
    towerLimit: 6,
    specialAbility: 'Freeze',
  },
  {
    key: 'volcanic',
    name: '',
    description: '',
    health: 800,
    damageResistance: 0.05,
    towerLimit: 10,
    specialAbility: 'Reflect',
  },
  {
    key: 'crystal',
    name: '',
    description: '',
    health: 600,
    damageResistance: 0.25,
    towerLimit: 4,
    specialAbility: 'GenerateEnergy',
  },
  {
    key: 'quantum',
    name: '',
    description: '',
    health: 900,
    damageResistance: 0.08,
    towerLimit: 7,
    specialAbility: 'Teleport',
  },
  {
    key: 'neutron',
    name: '',
    description: '',
    health: 2000,
    damageResistance: 0.15,
    towerLimit: 5,
    specialAbility: 'GravityField',
  },
];

export class EarthSelectionScene extends BaseScene {
  private userProfile: UserProfile;
  private selectedEarth: string = 'classic';

  constructor() {
    super({ key: 'EarthSelection' });
  }

  override create(): void {
    super.create();
    this.addBackground();
    
    // Load user profile
    this.userProfile = (window as any).userProfile;
    
    this.createModernEarthSelection();
  }

  private createModernEarthSelection(): void {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;
    
    // Header section
    this.createHeader();
    
    // Earth selection grid
    this.createEarthGrid();
    
    // Selection info panel
    this.createSelectionPanel();
    
    // Action buttons
    this.createActionButtons();
  }

  private createHeader(): void {
    const width = this.cameras.main.width;
    
    // Header background
    const headerBg = this.add.graphics();
    headerBg.fillGradientStyle(0x2c3e50, 0x34495e, 0x2c3e50, 0x34495e, 0.95);
    headerBg.fillRoundedRect(0, 0, width, 120, 0);
    headerBg.lineStyle(2, 0x27ae60, 0.8);
    headerBg.strokeRect(0, 118, width, 2);
    headerBg.setDepth(1001);
    
    // Title
    this.add.text(width / 2, 45, 'EARTH SELECTION', {
      fontSize: '36px',
      fontFamily: 'Arial Black',
      color: '#27ae60',
      fontStyle: 'bold'
    }).setOrigin(0.5).setDepth(1002);
    
    // Subtitle
    this.add.text(width / 2, 80, 'Choose your planet\'s defense configuration', {
      fontSize: '16px',
      fontFamily: 'Arial',
      color: '#bdc3c7',
      fontStyle: 'italic'
    }).setOrigin(0.5).setDepth(1002);
    
    // Back button
    this.createBackButton();
  }

  private createBackButton(): void {
    const buttonContainer = this.add.container(60, 60);
    buttonContainer.setDepth(1003);
    
    const buttonBg = this.add.graphics();
    buttonBg.fillGradientStyle(0x95a5a6, 0x7f8c8d, 0x95a5a6, 0x7f8c8d, 1);
    buttonBg.fillRoundedRect(-35, -20, 70, 40, 20);
    buttonBg.lineStyle(2, 0xbdc3c7, 0.8);
    buttonBg.strokeRoundedRect(-35, -20, 70, 40, 20);
    buttonContainer.add(buttonBg);
    
    const backIcon = this.add.text(-15, 0, '←', {
      fontSize: '20px',
      fontFamily: 'Arial Black',
      color: '#ffffff'
    }).setOrigin(0.5);
    buttonContainer.add(backIcon);
    
    const backText = this.add.text(8, 0, 'BACK', {
      fontSize: '12px',
      fontFamily: 'Arial Black',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    buttonContainer.add(backText);
    
    buttonContainer.setInteractive(
      new Phaser.Geom.Rectangle(-35, -20, 70, 40),
      Phaser.Geom.Rectangle.Contains
    );
    
    buttonContainer.on('pointerover', () => {
      this.tweens.add({
        targets: buttonContainer,
        scaleX: 1.05,
        scaleY: 1.05,
        duration: 200,
        ease: 'Power2'
      });
    });
    
    buttonContainer.on('pointerout', () => {
      this.tweens.add({
        targets: buttonContainer,
        scaleX: 1,
        scaleY: 1,
        duration: 200,
        ease: 'Power2'
      });
    });
    
    buttonContainer.on('pointerdown', () => {
      this.scene.start('MainMenu');
    });
  }

  private createEarthGrid(): void {
    const width = this.cameras.main.width;
    const startY = 150;
    
    // Grid container
    const gridContainer = this.add.container(width / 2, startY + 200);
    gridContainer.setDepth(1010);
    
    // Earth configurations
    const earthConfigs = [
      { key: 'classic', name: 'Classic Earth', icon: '🌍', color: 0x27ae60, health: 1000, towers: 8, ability: 'Balanced' },
      { key: 'ice', name: 'Ice Earth', icon: '❄️', color: 0x74b9ff, health: 1500, towers: 6, ability: 'Freeze' },
      { key: 'volcanic', name: 'Volcanic Earth', icon: '🔥', color: 0xe74c3c, health: 800, towers: 10, ability: 'Reflect' },
      { key: 'crystal', name: 'Crystal Earth', icon: '💎', color: 0x9b59b6, health: 600, towers: 4, ability: 'Energy Gen' },
      { key: 'quantum', name: 'Quantum Earth', icon: '⚛️', color: 0x3498db, health: 900, towers: 7, ability: 'Teleport' },
      { key: 'neutron', name: 'Neutron Earth', icon: '⭐', color: 0xf39c12, health: 2000, towers: 5, ability: 'Slow Field' }
    ];
    
    const cols = 3;
    const rows = 2;
    const spacing = 200;
    
    earthConfigs.forEach((earth, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      const x = (col - 1) * spacing;
      const y = (row - 0.5) * spacing;
      
      this.createEarthCard(earth, x, y, gridContainer);
    });
  }

  private createEarthCard(earth: any, x: number, y: number, container: Phaser.GameObjects.Container): void {
    const cardContainer = this.add.container(x, y);
    
    // Check if owned
    const isOwned = this.userProfile.ownedEarths.includes(earth.key);
    const isSelected = this.selectedEarth === earth.key;
    
    // Card background
    const cardBg = this.add.graphics();
    if (isSelected) {
      cardBg.fillGradientStyle(earth.color, earth.color * 0.8, earth.color, earth.color * 0.8, 0.9);
      cardBg.lineStyle(3, 0xffffff, 1);
    } else if (isOwned) {
      cardBg.fillGradientStyle(0x34495e, 0x2c3e50, 0x34495e, 0x2c3e50, 0.9);
      cardBg.lineStyle(2, earth.color, 0.8);
    } else {
      cardBg.fillStyle(0x34495e, 0.5);
      cardBg.lineStyle(2, 0x7f8c8d, 0.5);
    }
    
    cardBg.fillRoundedRect(-80, -80, 160, 160, 15);
    cardBg.strokeRoundedRect(-80, -80, 160, 160, 15);
    cardContainer.add(cardBg);
    
    // Lock overlay for unowned earths
    if (!isOwned) {
      const lockOverlay = this.add.graphics();
      lockOverlay.fillStyle(0x000000, 0.7);
      lockOverlay.fillRoundedRect(-80, -80, 160, 160, 15);
      cardContainer.add(lockOverlay);
      
      const lockIcon = this.add.text(0, 0, '🔒', {
        fontSize: '32px',
        fontFamily: 'Arial'
      }).setOrigin(0.5);
      cardContainer.add(lockIcon);
    } else {
      // Earth icon
      const earthIcon = this.add.text(0, -20, earth.icon, {
        fontSize: '48px',
        fontFamily: 'Arial'
      }).setOrigin(0.5);
      cardContainer.add(earthIcon);
      
      // Earth name
      const nameText = this.add.text(0, 15, earth.name, {
        fontSize: '12px',
        fontFamily: 'Arial Black',
        color: isSelected ? '#ffffff' : '#bdc3c7',
        fontStyle: 'bold',
        align: 'center'
      }).setOrigin(0.5);
      cardContainer.add(nameText);
      
      // Stats
      const statsText = this.add.text(0, 35, `HP: ${earth.health} | Towers: ${earth.towers}`, {
        fontSize: '8px',
        fontFamily: 'Arial',
        color: isSelected ? '#ffffff' : '#95a5a6',
        align: 'center'
      }).setOrigin(0.5);
      cardContainer.add(statsText);
      
      const abilityText = this.add.text(0, 50, earth.ability, {
        fontSize: '8px',
        fontFamily: 'Arial',
        color: earth.color,
        fontStyle: 'bold',
        align: 'center'
      }).setOrigin(0.5);
      cardContainer.add(abilityText);
    }
    
    // Interactive
    if (isOwned) {
      cardContainer.setInteractive(
        new Phaser.Geom.Rectangle(-80, -80, 160, 160),
        Phaser.Geom.Rectangle.Contains
      );
      
      cardContainer.on('pointerover', () => {
        if (!isSelected) {
          this.tweens.add({
            targets: cardContainer,
            scaleX: 1.05,
            scaleY: 1.05,
            duration: 200,
            ease: 'Power2'
          });
        }
      });
      
      cardContainer.on('pointerout', () => {
        if (!isSelected) {
          this.tweens.add({
            targets: cardContainer,
            scaleX: 1,
            scaleY: 1,
            duration: 200,
            ease: 'Power2'
          });
        }
      });
      
      cardContainer.on('pointerdown', () => {
        this.selectedEarth = earth.key;
        this.refreshEarthSelection();
      });
    }
    
    container.add(cardContainer);
    
    // Entrance animation
    cardContainer.setAlpha(0);
    cardContainer.setScale(0.8);
    
    this.time.delayedCall(100 * container.list.length, () => {
      this.tweens.add({
        targets: cardContainer,
        alpha: 1,
        scaleX: 1,
        scaleY: 1,
        duration: 400,
        ease: 'Back.easeOut'
      });
    });
  }

  private createSelectionPanel(): void {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;
    
    // Selection info panel
    const panelBg = this.add.graphics();
    panelBg.fillGradientStyle(0x2c3e50, 0x34495e, 0x2c3e50, 0x34495e, 0.95);
    panelBg.fillRoundedRect(50, height - 150, width - 100, 100, 15);
    panelBg.lineStyle(2, 0x27ae60, 0.8);
    panelBg.strokeRoundedRect(50, height - 150, width - 100, 100, 15);
    panelBg.setDepth(1020);
    
    // Selected earth info
    this.add.text(width / 2, height - 130, 'SELECTED CONFIGURATION', {
      fontSize: '16px',
      fontFamily: 'Arial Black',
      color: '#27ae60',
      fontStyle: 'bold'
    }).setOrigin(0.5).setDepth(1021);
    
    const selectedConfig = EARTH_OPTIONS.find(e => e.key === this.selectedEarth);
    if (selectedConfig) {
      this.add.text(width / 2, height - 100, `${selectedConfig.name} - ${selectedConfig.description}`, {
        fontSize: '14px',
        fontFamily: 'Arial',
        color: '#bdc3c7',
        align: 'center'
      }).setOrigin(0.5).setDepth(1021);
    }
  }

  private createActionButtons(): void {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;
    
    // Start Game button
    const startButtonContainer = this.add.container(width / 2, height - 60);
    startButtonContainer.setDepth(1025);
    
    const startButtonBg = this.add.graphics();
    startButtonBg.fillGradientStyle(0x27ae60, 0x2ecc71, 0x27ae60, 0x2ecc71, 1);
    startButtonBg.fillRoundedRect(-100, -25, 200, 50, 25);
    startButtonBg.lineStyle(3, 0xffffff, 1);
    startButtonBg.strokeRoundedRect(-100, -25, 200, 50, 25);
    startButtonContainer.add(startButtonBg);
    
    const startIcon = this.add.text(-20, 0, '🚀', {
      fontSize: '24px',
      fontFamily: 'Arial'
    }).setOrigin(0.5);
    startButtonContainer.add(startIcon);
    
    const startText = this.add.text(20, 0, 'START MISSION', {
      fontSize: '16px',
      fontFamily: 'Arial Black',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    startButtonContainer.add(startText);
    
    startButtonContainer.setInteractive(
      new Phaser.Geom.Rectangle(-100, -25, 200, 50),
      Phaser.Geom.Rectangle.Contains
    );
    
    startButtonContainer.on('pointerover', () => {
      this.tweens.add({
        targets: startButtonContainer,
        scaleX: 1.05,
        scaleY: 1.05,
        duration: 200,
        ease: 'Power2'
      });
    });
    
    startButtonContainer.on('pointerout', () => {
      this.tweens.add({
        targets: startButtonContainer,
        scaleX: 1,
        scaleY: 1,
        duration: 200,
        ease: 'Power2'
      });
    });
    
    startButtonContainer.on('pointerdown', () => {
      // Save selected earth to profile
      this.userProfile.equippedEarth = this.selectedEarth;
      (window as any).userProfile = this.userProfile;
      
      this.scene.start('GamePlay');
    });
  }

  private refreshEarthSelection(): void {
    // Restart scene to refresh selection
    this.scene.restart();
  }
} 